import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    OnDestroy,
    OnInit,
    ViewChild
} from "@angular/core";
import {FormControl} from "@angular/forms";
import {takeUntil} from "rxjs/operators";
import {ConfirmComponent} from "../../../../../../common/components/confirm/confirm.component";
import {Api} from "../../../../../../common/services/api.service";
import {Api3Service} from "../../../../../../common/services/api3.service";
import {ToastService} from "../../../../../../common/services/toast.service";
import {SpinnerService} from "../../../../../../common/services/spinner.service";
import {Modal, ModalService} from "../../../../../services/modal.service";
import {Base} from "../../../../../../common/interfaces/base.interfaces";
import {Table} from "../../../../../../common/interfaces/table.interface";
import {Table2Component} from "../../../../../../common/components/table2";
import {LogiviceV3WebhookHostsFormComponent} from "../form/form.component";

@Component({
    selector: "section-logivice-v3-webhook-hosts-list",
    template: `
        <common-confirm></common-confirm>

        <mat-card>
            <mat-card-content>
                <common-table2 *ngIf="listTable" [settings]="listTable">
                    <div row1>
                        <a mat-raised-button color="primary" (click)="openForm()">
                            Add New
                        </a>
                        <mat-checkbox [formControl]="only_active">Only active</mat-checkbox>
                    </div>
                </common-table2>
            </mat-card-content>
        </mat-card>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LogiviceV3WebhookHostsListComponent implements Base.IComponent, OnInit, OnDestroy {

    private destroy$: EventEmitter<boolean> = new EventEmitter<boolean>();

    public readonly state: Base.IState;

    @ViewChild(ConfirmComponent, {static: false})
    public confirmRef: ConfirmComponent;

    @ViewChild(Table2Component, {static: false})
    public tableRef: Table2Component;

    public listTable: Table.ISettings;

    public only_active: FormControl = new FormControl(true);

    public constructor(
        private api3Service: Api3Service,
        private toastService: ToastService,
        private modalService: ModalService,
        private spinnerService: SpinnerService,
        private changeDetectorRef: ChangeDetectorRef
    ) {
    }

    private getUrl(): Table.ITableApi {
        return {
            url: [
                this.state.section,
                "whitelist-ips"
            ],
            query: {
                only_active: this.only_active.value,
                appends: ["type"]
            },
            version: 3
        };
    }

    private prepareList(): void {
        this.listTable = {
            actions: [
                {
                    name: "edit",
                    title: "Edit",
                    click: (row: any): void => {
                        this.openForm(row);
                    }
                },
                {
                    name: "delete",
                    title: "Delete",
                    click: (row: any): void => {
                        this.deleteHost(row);
                    }
                },
            ],
            columns: [
                {
                    data: "name",
                    title: "Name"
                },
                {
                    data: "value",
                    title: "IP / URL"
                },
                {
                    data: "type",
                    title: "Type",
                    sortable: false,
                    searchable: false,
                    render: (row: any): string => {
                        return row.type === "LogiviceV3" ? "Logivice V3" : (row.type || "");
                    }
                },
                {
                    data: "is_active",
                    title: "Active",
                    render: (row: any): string => {
                        return row.is_active ? "Yes" : "No";
                    }
                },
                {
                    data: "created_at",
                    title: "Created"
                },
                {
                    data: "updated_at",
                    title: "Updated"
                }
            ],
            api: this.getUrl()
        };
        this.changeDetectorRef.markForCheck();
    }

    private async deleteHost(host: { id: number; name: string }): Promise<any> {
        if (!await this.confirmRef.confirm(`Delete "${host.name}"?`)) {
            return;
        }
        this.spinnerService.show();
        const {code, message}: Api.IResponse = await this.api3Service.delete(
            `${this.state.section}/whitelist-ips/${host.id}`
        );
        this.spinnerService.hide();
        if (code === 200) {
            this.toastService.show(message, "success");
            this.tableRef.reload(this.getUrl());
        }
    }

    public async openForm(data: any = null): Promise<any> {
        const response: Modal.IResponse = await this.modalService.open(LogiviceV3WebhookHostsFormComponent, {
            data,
            state: this.state
        });

        if (response) {
            this.tableRef.reload(this.getUrl());
        }
    }

    public ngOnInit(): void {
        this.prepareList();

        this.only_active.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.tableRef.reload(this.getUrl());
        });
    }

    public ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    public ngConfig(): Base.IConfig {
        return {
            name: "whitelist-ips",
            actions: {
                "browse": ["browse_admin"]
            }
        };
    }
}
